import { useState } from 'react';
import { X, Sparkles, Flame, Percent, Package, PackageX, ArrowUpDown, ArrowUp, ArrowDown, Clock, TrendingUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type SortOption = 'default' | 'price_asc' | 'price_desc' | 'date_desc' | 'date_asc' | 'popular';

export interface ProductFiltersState {
  showNewOnly: boolean;
  showBestsellersOnly: boolean;
  showDiscountOnly: boolean;
  showInStockOnly: boolean;
  showOutOfStockOnly: boolean;
  priceMin: string;
  priceMax: string;
  sortBy: SortOption;
}

export const defaultFilters: ProductFiltersState = {
  showNewOnly: false,
  showBestsellersOnly: false,
  showDiscountOnly: false,
  showInStockOnly: false,
  showOutOfStockOnly: false,
  priceMin: '',
  priceMax: '',
  sortBy: 'default',
};

interface ProductFiltersProps {
  filters: ProductFiltersState;
  onFiltersChange: (filters: ProductFiltersState) => void;
  variant?: 'catalog' | 'admin';
  className?: string;
}

const ProductFilters = ({ 
  filters, 
  onFiltersChange, 
  variant = 'catalog',
  className 
}: ProductFiltersProps) => {
  const [priceOpen, setPriceOpen] = useState(false);
  
  const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'priceMin' || key === 'priceMax') return value !== '';
    if (key === 'sortBy') return value !== 'default';
    return value === true;
  }).length;

  const handleToggle = (key: keyof ProductFiltersState) => {
    if (typeof filters[key] === 'boolean') {
      if (key === 'showInStockOnly' && !filters.showInStockOnly) {
        onFiltersChange({ ...filters, showInStockOnly: true, showOutOfStockOnly: false });
      } else if (key === 'showOutOfStockOnly' && !filters.showOutOfStockOnly) {
        onFiltersChange({ ...filters, showOutOfStockOnly: true, showInStockOnly: false });
      } else {
        onFiltersChange({ ...filters, [key]: !filters[key] });
      }
    }
  };

  const handlePriceChange = (key: 'priceMin' | 'priceMax', value: string) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleSortChange = (value: SortOption) => {
    onFiltersChange({ ...filters, sortBy: value });
  };

  const resetFilters = () => {
    onFiltersChange(defaultFilters);
  };

  const sortOptions = [
    { value: 'default' as const, label: 'По умолчанию', icon: ArrowUpDown },
    { value: 'price_asc' as const, label: 'Сначала дешёвые', icon: ArrowUp },
    { value: 'price_desc' as const, label: 'Сначала дорогие', icon: ArrowDown },
    { value: 'date_desc' as const, label: 'Сначала новые', icon: Clock },
    { value: 'date_asc' as const, label: 'Сначала старые', icon: Clock },
    { value: 'popular' as const, label: 'По популярности', icon: TrendingUp },
  ];

  const currentSort = sortOptions.find(s => s.value === filters.sortBy) || sortOptions[0];

  return (
    <div className={cn("space-y-3", className)}>
      {/* Filter chips row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Sort Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full text-xs md:text-sm gap-1.5 h-8 px-3 bg-background"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              {currentSort.label}
              <ChevronDown className="w-3 h-3 ml-0.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48 bg-popover">
            {sortOptions.map(({ value, label, icon: Icon }) => (
              <DropdownMenuItem
                key={value}
                onClick={() => handleSortChange(value)}
                className={cn(
                  "flex items-center gap-2",
                  filters.sortBy === value && "bg-accent"
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* New badge filter */}
        <Button
          variant={filters.showNewOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleToggle('showNewOnly')}
          className={cn(
            "rounded-full text-xs md:text-sm gap-1.5 h-8",
            filters.showNewOnly 
              ? 'bg-info text-info-foreground hover:bg-info/90'
              : 'border-info/50 text-info hover:border-info hover:bg-info/10'
          )}
        >
          <Sparkles className="w-3.5 h-3.5" />
          Новинки
        </Button>

        {/* Bestsellers filter */}
        <Button
          variant={filters.showBestsellersOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleToggle('showBestsellersOnly')}
          className={cn(
            "rounded-full text-xs md:text-sm gap-1.5 h-8",
            filters.showBestsellersOnly 
              ? 'bg-warning text-warning-foreground hover:bg-warning/90'
              : 'border-warning/50 text-warning hover:border-warning hover:bg-warning/10'
          )}
        >
          <Flame className="w-3.5 h-3.5" />
          Хиты
        </Button>

        {/* Discount filter */}
        <Button
          variant={filters.showDiscountOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleToggle('showDiscountOnly')}
          className={cn(
            "rounded-full text-xs md:text-sm gap-1.5 h-8",
            filters.showDiscountOnly 
              ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              : 'border-destructive/50 text-destructive hover:border-destructive hover:bg-destructive/10'
          )}
        >
          <Percent className="w-3.5 h-3.5" />
          Акции
        </Button>

        {/* In stock filter */}
        <Button
          variant={filters.showInStockOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleToggle('showInStockOnly')}
          className={cn(
            "rounded-full text-xs md:text-sm gap-1.5 h-8",
            filters.showInStockOnly 
              ? 'bg-eco text-eco-foreground hover:bg-eco/90'
              : 'border-eco/50 text-eco hover:border-eco hover:bg-eco/10'
          )}
        >
          <Package className="w-3.5 h-3.5" />
          В наличии
        </Button>

        {/* Out of stock filter (admin only) */}
        {variant === 'admin' && (
          <Button
            variant={filters.showOutOfStockOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleToggle('showOutOfStockOnly')}
            className={cn(
              "rounded-full text-xs md:text-sm gap-1.5 h-8",
              filters.showOutOfStockOnly 
                ? 'bg-muted text-muted-foreground hover:bg-muted/90'
                : 'border-muted-foreground/50 text-muted-foreground hover:border-muted-foreground hover:bg-muted/10'
            )}
          >
            <PackageX className="w-3.5 h-3.5" />
            Нет в наличии
          </Button>
        )}

        {/* Price filter dropdown */}
        <Popover open={priceOpen} onOpenChange={setPriceOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "rounded-full text-xs md:text-sm gap-1.5 h-8",
                (filters.priceMin || filters.priceMax) && "border-primary text-primary"
              )}
            >
              Цена
              {(filters.priceMin || filters.priceMax) && (
                <span className="text-[10px] bg-primary/10 px-1.5 py-0.5 rounded-full">
                  {filters.priceMin || '0'} — {filters.priceMax || '∞'}
                </span>
              )}
              <ChevronDown className="w-3 h-3 ml-0.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-3 bg-popover">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-muted-foreground">Цена от</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={filters.priceMin}
                    onChange={(e) => handlePriceChange('priceMin', e.target.value)}
                    className="h-8 text-sm"
                    min={0}
                  />
                </div>
                <span className="text-muted-foreground mt-5">—</span>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-muted-foreground">Цена до</Label>
                  <Input
                    type="number"
                    placeholder="∞"
                    value={filters.priceMax}
                    onChange={(e) => handlePriceChange('priceMax', e.target.value)}
                    className="h-8 text-sm"
                    min={0}
                  />
                </div>
              </div>
              <Button 
                size="sm" 
                className="w-full h-8"
                onClick={() => setPriceOpen(false)}
              >
                Применить
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        
        {/* Reset button */}
        {activeFiltersCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className="rounded-full text-xs md:text-sm gap-1 text-muted-foreground hover:text-foreground h-8"
          >
            <X className="w-3 h-3" />
            Сбросить
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductFilters;
